/**
 * Deep-Link Utilities for GPU Instance Creation
 *
 * URL Format:
 * https://app.float16.cloud?action=gpu-instance&type=one-click-deploy&model=typhoon-ocr-2b
 */

// Storage key for deep-link intent
const DEEP_LINK_INTENT_KEY = 'float16_deep_link_intent';

// Intent expiry time (30 minutes)
const INTENT_EXPIRY_MS = 30 * 60 * 1000;

// Valid action types
export type DeepLinkAction = 'gpu-instance';

// Valid deployment types
export type DeepLinkDeploymentType = 'one-click-deploy' | 'base-vm';

// Deep-link intent structure
export interface DeepLinkIntent {
  action: DeepLinkAction;
  type?: DeepLinkDeploymentType;
  model?: string;
  timestamp: number;
}

// Model aliases mapping - maps URL-friendly aliases to actual model IDs
// The model ID should match the `id` field in DEPLOYABLE_MODELS
const MODEL_ALIASES: Record<string, string> = {
  // Typhoon models
  'typhoon-ocr1.5-2b': 'Typhoon-ocr1.5-2b',
  'typhoon-ocr-2b': 'Typhoon-ocr1.5-2b',
  'typhoon-ocr': 'Typhoon-ocr1.5-2b',
  'typhoon2.5-30b': 'Typhoon2.5-qwen3-30b-a3b',
  // Llama models
  'llama3.3-70b': 'llama3.3-70b',
  'llama-70b': 'llama3.3-70b',
  // GPT-OSS models
  'gpt-oss-120b': 'GPT-OSS-120B',
  'gpt-oss-20b': 'GPT-OSS-20B',
  // Qwen models
  'qwen3-vl-235b': 'Qwen3-VL-235B-A22B-Instruct',
  'qwen3-vl-32b': 'Qwen3-VL-32B-Instruct',
  'qwen3-vl-8b': 'Qwen3-VL-8B-Instruct',
  // GLM models
  'glm-4.7-flash': 'GLM-4.7-Flash',
};

/**
 * Resolve model alias to actual model ID
 */
export const resolveModelAlias = (alias: string): string => {
  // First check if it's a known alias
  const resolved = MODEL_ALIASES[alias.toLowerCase()];
  if (resolved) return resolved;

  // If not an alias, return as-is (might be a direct model ID)
  return alias;
};

/**
 * Check if the current URL has deep-link parameters
 */
export const hasDeepLinkParams = (): boolean => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('action') === 'gpu-instance';
};

/**
 * Parse deep-link parameters from URL
 */
export const parseDeepLinkParams = (): DeepLinkIntent | null => {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const action = params.get('action');

  // Only handle gpu-instance action for now
  if (action !== 'gpu-instance') return null;

  const type = params.get('type') as DeepLinkDeploymentType | null;
  const model = params.get('model');

  return {
    action: 'gpu-instance',
    type: type || undefined,
    model: model ? resolveModelAlias(model) : undefined,
    timestamp: Date.now(),
  };
};

/**
 * Store deep-link intent in sessionStorage
 */
export const storeDeepLinkIntent = (intent: DeepLinkIntent): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(DEEP_LINK_INTENT_KEY, JSON.stringify(intent));
};

/**
 * Retrieve stored deep-link intent from sessionStorage
 * Returns null if expired or not found
 */
export const getDeepLinkIntent = (): DeepLinkIntent | null => {
  if (typeof window === 'undefined') return null;

  const stored = sessionStorage.getItem(DEEP_LINK_INTENT_KEY);
  if (!stored) return null;

  try {
    const intent = JSON.parse(stored) as DeepLinkIntent;

    // Check if expired (30 minutes)
    if (Date.now() - intent.timestamp > INTENT_EXPIRY_MS) {
      clearDeepLinkIntent();
      return null;
    }

    return intent;
  } catch {
    clearDeepLinkIntent();
    return null;
  }
};

/**
 * Clear stored deep-link intent
 */
export const clearDeepLinkIntent = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(DEEP_LINK_INTENT_KEY);
};

/**
 * Clean deep-link parameters from URL without page reload
 */
export const cleanDeepLinkUrl = (): void => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Remove deep-link specific params
  params.delete('action');
  params.delete('type');
  params.delete('model');

  // Update URL without reload
  const newUrl = params.toString()
    ? `${url.pathname}?${params.toString()}`
    : url.pathname;

  window.history.replaceState({}, '', newUrl);
};

/**
 * Capture deep-link intent from URL
 * Parses URL params, stores in sessionStorage, and cleans URL
 * Returns the captured intent or null if no deep-link params
 */
export const captureDeepLinkIntent = (): DeepLinkIntent | null => {
  if (!hasDeepLinkParams()) return null;

  const intent = parseDeepLinkParams();
  if (!intent) return null;

  storeDeepLinkIntent(intent);
  cleanDeepLinkUrl();

  return intent;
};

/**
 * Build target URL for GPU instance create page with deep-link params
 */
export const buildDeepLinkTargetUrl = (
  orgId: string,
  workspaceId: string,
  intent: DeepLinkIntent
): string => {
  const basePath = `/dashboard/${orgId}/${workspaceId}/gpu-instance/create`;
  const params = new URLSearchParams();

  if (intent.type) {
    params.set('deploymentType', intent.type);
  }
  if (intent.model) {
    params.set('model', intent.model);
  }

  return params.toString() ? `${basePath}?${params.toString()}` : basePath;
};
